import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { deliveryModel } from "../../models/reportsModel";
import {
  getDeliveryAttachmentsList,
  getDeliveryReportV2,
} from "../dispatcher/deliveryDispatcher"; // adjust path

// ---------------- Delivery ----------------

interface DeliveryState {
  deliveryList: Array<deliveryModel>;
  isDeliveryListLoaded: boolean;
  selectedDelivery: deliveryModel;
  isDeliveryTableRefreshed: boolean;
  allowedRolesForDelivery: Array<string>;
  deliveryListFilterCategoryList: Array<string>;

  // Report State Params
  loadingReportV2: boolean;
  totalRows: number | null;
  totalPages: number | null;
  page: number;
  pageSize: number;
  sort: any;
  search: string;
  city: string;
  profile: string;
  salesName: string;
  onlyToday: boolean;
  fetchAll: boolean;
  dateFrom: string | null;
  dateTo: string | null;

  states: string[];
  statuses: string[];

  // Attachments Recent Uploads Params
  loadingAttachments: boolean;
  attachmentsList: Array<any>;
  attachmentsTotalRows: number | null;
  attachmentsTotalPages: number | null;
  attachmentsPage: number;
  attachmentsPageSize: number;
  attachmentsSort: string;
  attachmentsSearch: string;
}

const deliveryInitialState: DeliveryState = {
  deliveryList: [],
  isDeliveryListLoaded: false,
  selectedDelivery: {} as deliveryModel,
  isDeliveryTableRefreshed: false,

  // Constants — same as original deliveryState
  deliveryListFilterCategoryList: [
    "all",
    "NSW",
    "VIC",
    "QLD",
    "WA",
    "SA",
    "TAS",
    "Today",
    "Next 7 days",
    "Next 14 days",
    "Next 30 days",
  ],
  allowedRolesForDelivery: ["OWNER", "ADMIN", "MANAGER", "OPERATION"],

  // Delivery Report V2
  loadingReportV2: false,
  totalRows: null,
  totalPages: null,
  page: 1,
  pageSize: 20,
  sort: {
    key: "RequestDate",
    direction: "desc",
  },
  search: "",
  city: "all",
  profile: "all",
  salesName: "",
  onlyToday: true,
  fetchAll: false,
  dateFrom: null,
  dateTo: null,

  states: [],
  statuses: [],

  loadingAttachments: false,
  attachmentsList: [],
  attachmentsTotalRows: null,
  attachmentsTotalPages: null,
  attachmentsPage: 1,
  attachmentsPageSize: 10,
  attachmentsSort: "-createdAt",
  attachmentsSearch: "",
};

interface DeliveryAttachmentFlagPayload {
  docNum: string | number;
  hasAdditionalAttachment: boolean;
  files?: string[];
  remarks?: string;
  uploadedBy?: string;
  updatedAt?: string;
}

export const deliverySlice = createSlice({
  name: "delivery",
  initialState: deliveryInitialState,
  reducers: {
    setDeliveryList: (state, action: PayloadAction<Array<deliveryModel>>) => {
      state.deliveryList = action.payload;
      state.isDeliveryListLoaded = true;
    },
    setIsDeliveryTableRefreshed: (state, action: PayloadAction<boolean>) => {
      state.isDeliveryTableRefreshed = action.payload;
    },
    setSelectedDelivery: (state, action: PayloadAction<deliveryModel>) => {
      state.selectedDelivery = action.payload;
    },
    updateReportV2States: (state, action: PayloadAction<string[]>) => {
      state.states = action.payload;
    },
    updateReportV2Statuses: (state, action: PayloadAction<string[]>) => {
      state.statuses = action.payload;
    },
    updateDeliveryAttachmentFlag: (
      state,
      action: PayloadAction<DeliveryAttachmentFlagPayload>,
    ) => {
      const {
        docNum,
        hasAdditionalAttachment,
        files,
        remarks,
        uploadedBy,
        updatedAt,
      } = action.payload;
      const now = updatedAt || new Date().toISOString();

      state.deliveryList = state.deliveryList.map((item: any) =>
        item.DocNum === docNum
          ? {
              ...item,
              hasAdditionalAttachment,
              attachmentFiles: files ?? item.attachmentFiles,
              attachmentRemarks: remarks ?? item.attachmentRemarks,
              attachmentUploadedBy: uploadedBy ?? item.attachmentUploadedBy,
              attachmentUpdatedAt: now,
            }
          : item,
      );
    },

    // ---- Delivery Report V2 pagination/filter controls ----
    updateReportV2Page: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    updateReportV2PageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
    },
    updateReportV2Sort: (state, action: PayloadAction<any>) => {
      state.sort = action.payload;
    },
    searchReportV2: (state, action: PayloadAction<string>) => {
      state.search = action.payload;
    },
    updateReportV2City: (state, action: PayloadAction<string>) => {
      state.city = action.payload;
    },
    updateReportV2DateRange: (
      state,
      action: PayloadAction<{ dateFrom: string | null; dateTo: string | null }>,
    ) => {
      state.dateFrom = action.payload.dateFrom;
      state.dateTo = action.payload.dateTo;
    },
    updateReportV2OnlyToday: (state, action: PayloadAction<boolean>) => {
      state.onlyToday = action.payload;
    },
    updateReportV2FetchAll: (state, action: PayloadAction<boolean>) => {
      state.fetchAll = action.payload;
    },

    // Recent Uploads state for attachments info
    updateAttachmentsPage: (state, action: PayloadAction<number>) => {
      state.attachmentsPage = action.payload;
    },
    updateAttachmentsPageSize: (state, action: PayloadAction<number>) => {
      state.attachmentsPageSize = action.payload;
    },
    updateAttachmentsSort: (state, action: PayloadAction<string>) => {
      state.attachmentsSort = action.payload;
      state.attachmentsPage = 1; // reset to first page on sort change
    },
    searchAttachments: (state, action: PayloadAction<string>) => {
      state.attachmentsSearch = action.payload;
      state.attachmentsPage = 1; // reset to first page on new search
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDeliveryReportV2.pending, (state) => {
        state.loadingReportV2 = true;
      })
      .addCase(getDeliveryReportV2.fulfilled, (state, action) => {
        state.loadingReportV2 = false;
        state.deliveryList = action.payload.data;
        state.isDeliveryListLoaded = true;

        // pagination object shape matches the MAPI deliveryReportV2 response
        state.totalRows = action.payload.pagination.totalRows;
        state.totalPages = action.payload.pagination.totalPages;
        // state.page = action.payload.pagination.page;
        // state.pageSize = action.payload.pagination.pageSize;
      })
      .addCase(getDeliveryReportV2.rejected, (state) => {
        state.loadingReportV2 = false;
      })
      // ---- Recent Uploads / Attachments ----
      .addCase(getDeliveryAttachmentsList.pending, (state) => {
        state.loadingAttachments = true;
      })
      .addCase(getDeliveryAttachmentsList.fulfilled, (state, action) => {
        state.loadingAttachments = false;

        const raw = action.payload;
        const dataArray = Array.isArray(raw) ? raw : raw?.data || [];

        state.attachmentsList = dataArray.map((item: any) => ({
          docNum: item.DocNum,
          files: item.attachment ? item.attachment.split(",") : [],
          remarks: item.remarks || "-",
          createdAt: item.createdAt,
          updatedAt: item.updatedAt,
        }));

        state.attachmentsTotalRows =
          raw?.pagination?.totalRows ?? dataArray.length;
        state.attachmentsTotalPages = raw?.pagination?.totalPages ?? 1;
      })
      .addCase(getDeliveryAttachmentsList.rejected, (state) => {
        state.loadingAttachments = false;
      });
  },
});

// ---------------- Stock Transfer ---------------- (unchanged, no pagination)

interface StockTransferState {
  stockTransferList: Array<deliveryModel>;
  isStockTransferListLoaded: boolean;
  selectedStockTransfer: deliveryModel;
  isStockTransferTableRefreshed: boolean;
}

const stockTransferInitialState: StockTransferState = {
  stockTransferList: [],
  isStockTransferListLoaded: false,
  selectedStockTransfer: {} as deliveryModel,
  isStockTransferTableRefreshed: false,
};

export const stockTransferSlice = createSlice({
  name: "stockTransfer",
  initialState: stockTransferInitialState,
  reducers: {
    setStockTransferList: (
      state,
      action: PayloadAction<Array<deliveryModel>>,
    ) => {
      state.stockTransferList = action.payload;
      state.isStockTransferListLoaded = true;
    },
    setIsStockTransferTableRefreshed: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.isStockTransferTableRefreshed = action.payload;
    },
    setSelectedStockTransfer: (state, action: PayloadAction<deliveryModel>) => {
      state.selectedStockTransfer = action.payload;
    },
  },
});

// ---------------- Exports ----------------
export const {
  setDeliveryList,
  setIsDeliveryTableRefreshed,
  setSelectedDelivery,
  updateDeliveryAttachmentFlag,
  updateReportV2Page,
  updateReportV2PageSize,
  updateReportV2Sort,
  searchReportV2,
  updateReportV2City,
  updateReportV2DateRange,
  updateReportV2States,
  updateReportV2Statuses,
  updateReportV2OnlyToday,
  updateReportV2FetchAll,
  updateAttachmentsPage,
  updateAttachmentsPageSize,
  updateAttachmentsSort,
  searchAttachments,
} = deliverySlice.actions;

export const {
  setStockTransferList,
  setIsStockTransferTableRefreshed,
  setSelectedStockTransfer,
} = stockTransferSlice.actions;

export const deliveryReducer = deliverySlice.reducer;
export const stockTransferReducer = stockTransferSlice.reducer;
